import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Save, FileText, Plus, Trash2 } from 'lucide-react'

const LOC_OPTIONS = ['HQ Office', 'Remote', 'Data Center', 'Cloud (AWS)', 'Cloud (Azure)', 'Cloud (GCP)', 'Branch Office']

function TagInput({ values, onChange, suggestions, placeholder }) {
  const [input, setInput] = useState('')
  const add = (val) => {
    const v = val.trim()
    if (v && !values.includes(v)) onChange([...values, v])
    setInput('')
  }
  const remove = (v) => onChange(values.filter(x => x !== v))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {values.map(v => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'var(--accent-dim)', color: 'var(--accent)',
            border: '1px solid rgba(16,185,129,0.3)', borderRadius: 100,
            padding: '3px 10px', fontSize: '0.8125rem', fontWeight: 500
          }}>
            {v}
            <button onClick={() => remove(v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', display: 'flex', padding: 0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="form-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add(input))}
          placeholder={placeholder}
          style={{ flex: 1 }}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => add(input)} disabled={!input.trim()}>Add</button>
      </div>
      {suggestions && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {suggestions.filter(s => !values.includes(s)).map(s => (
            <button key={s} className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '3px 8px', color: 'var(--text-muted)' }} onClick={() => add(s)}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function Scope({ toast }) {
  const qc = useQueryClient()
  const { data: scope, isLoading } = useQuery({ queryKey: ['scope'], queryFn: api.getScope })
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })
  const { data: processes = [] } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })

  const [form, setForm] = useState({
    scope_name: '',
    description: '',
    locations: [],
    third_parties: [],
    status: 'Draft',
  })

  useEffect(() => {
    if (scope) {
      setForm({
        scope_name: scope.scope_name || '',
        description: scope.description || '',
        locations: JSON.parse(scope.locations || '[]'),
        third_parties: JSON.parse(scope.third_parties || '[]'),
        status: scope.status || 'Draft',
      })
    }
  }, [scope])

  const saveMut = useMutation({
    mutationFn: () => api.upsertScope({
      ...form,
      departments: '[]', // Removed from UI, sending default empty for DB
      applications: '[]', // Removed from UI
      excluded_entities: '[]', // Removed from UI
      exclusion_justification: '', // Removed from UI
      locations: JSON.stringify(form.locations),
      third_parties: JSON.stringify(form.third_parties),
    }),
    onSuccess: () => { qc.invalidateQueries(['scope']); toast('Scope saved', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  if (isLoading) return <div className="page"><p className="text-muted">Loading…</p></div>

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Scope Definition</h1>
          <p>ISO 27001 Clause 4.3 — Define the ISMS boundary and applicability</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="form-select" style={{ width: 'auto' }} value={form.status} onChange={e => set('status', e.target.value)}>
            <option>Draft</option>
            <option>Under Review</option>
            <option>Approved</option>
          </select>
          <button className="btn btn-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            <Save size={15} /> {saveMut.isPending ? 'Saving…' : 'Save Scope'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Basic Info */}
        <div className="card">
          <h3 style={{ fontSize: '1rem', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color="var(--accent)" /> Scope Statement
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Scope Name / Title *</label>
              <input className="form-input" value={form.scope_name} onChange={e => set('scope_name', e.target.value)} placeholder="e.g. ISMS Scope — Core IT Systems & Cloud Infrastructure" />
            </div>
            <div className="form-group">
              <label className="form-label">Scope Description</label>
              <textarea className="form-textarea" style={{ minHeight: 110 }} value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe the boundaries of the ISMS. Include what is covered, the nature of the business, and the type of information handled…" />
            </div>
          </div>
        </div>

        {/* Inclusions */}
        <div className="card">
          <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>Included Entities</h3>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>Assets, processes, locations, and vendors within scope</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Display all fetched Assets and Processes */}
            <div className="form-group">
              <label className="form-label">In-Scope Assets</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {assets.length === 0 ? <span className="text-muted text-sm">No assets registered yet.</span> : null}
                {assets.map(a => (
                  <span key={a.id} style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: 'var(--bg-elevated)', color: 'var(--text)',
                    border: '1px solid var(--border)', borderRadius: 4,
                    padding: '4px 10px', fontSize: '0.8125rem'
                  }}>
                    {a.asset_name} ({a.asset_type})
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted" style={{ marginTop: 6 }}>Assets are automatically included from the Asset Register.</p>
            </div>

            <div className="form-group">
              <label className="form-label">In-Scope Processes</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {processes.length === 0 ? <span className="text-muted text-sm">No processes registered yet.</span> : null}
                {processes.map(p => (
                  <span key={p.id} style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: 'var(--bg-elevated)', color: 'var(--text)',
                    border: '1px solid var(--border)', borderRadius: 4,
                    padding: '4px 10px', fontSize: '0.8125rem'
                  }}>
                    {p.process_name}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted" style={{ marginTop: 6 }}>Processes are automatically included from the Process List.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Locations</label>
              <TagInput values={form.locations} onChange={v => set('locations', v)} suggestions={LOC_OPTIONS} placeholder="Type location and press Enter" />
            </div>

            <div className="form-group">
              <label className="form-label">Third Parties / Vendors</label>
              <TagInput values={form.third_parties} onChange={v => set('third_parties', v)} placeholder="e.g. AWS, Salesforce, Payroll Provider…" />
            </div>
          </div>
        </div>

        {/* Preview */}
        {(form.scope_name || form.description) && (
          <div className="card" style={{ background: 'var(--bg-base)', borderStyle: 'dashed' }}>
            <h3 style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Scope Document Preview</h3>
            <div style={{ lineHeight: 1.8 }}>
              {form.scope_name && <h2 style={{ fontSize: '1.125rem', marginBottom: 8 }}>{form.scope_name}</h2>}
              {form.description && <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', marginBottom: 16 }}>{form.description}</p>}
              
              {assets.length > 0 && <p style={{ fontSize: '0.875rem' }}><strong>Assets:</strong> <span style={{ color: 'var(--text-secondary)' }}>{assets.length} items from Asset Register</span></p>}
              {processes.length > 0 && <p style={{ fontSize: '0.875rem' }}><strong>Processes:</strong> <span style={{ color: 'var(--text-secondary)' }}>{processes.length} defined processes</span></p>}
              
              {form.locations.length > 0 && <p style={{ fontSize: '0.875rem' }}><strong>Locations:</strong> <span style={{ color: 'var(--text-secondary)' }}>{form.locations.join(', ')}</span></p>}
              {form.third_parties.length > 0 && <p style={{ fontSize: '0.875rem' }}><strong>Third Parties / Vendors:</strong> <span style={{ color: 'var(--text-secondary)' }}>{form.third_parties.join(', ')}</span></p>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
